import { Box, Typography } from "@mui/material";
import {
  DataGridProProps,
  GridApiPro,
  GridBasicGroupNode,
  GridEventListener,
  useGridApiRef,
} from "@mui/x-data-grid-pro";
import { CalculatorRatelistInput, MarginCategory } from "@sinch/types/sinch.types";
import clsx from "clsx";
import * as React from "react";
import { FC, MutableRefObject, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CalculationDataGrid,
  getRowLevelClassName,
} from "@sinch/components/application/RatesCompositionTable/CalculationDataGrid";
import { CustomGridTreeDataGroupingCell } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/CustomGridTreeDataGroupingCell";
import { useChildrenGetter } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/hooks/useChildrenGetter";
import { useLabelGetter } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/hooks/useLabelGetter";
import { useRatesColumns } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/hooks/useRatesColumns";
import { CategoryFilter } from "@sinch/components/application/RatesCompositionTable/CategoryFilter";
import {
  TOTAL_ROW_ID,
  useTreeQueries,
} from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/hooks/useTreeQueries";
import { useTreeRows } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/hooks/useTreeRows";
import { EntityName } from "@sinch/components/application/RatesCompositionTable/EntityOverviewTable/rowBuilders";

import {
  InternalRateComponentsMarginTable,
  RateComponentComposition,
} from "@sinch/components/application/RatesCompositionTable/InternalRateComponentsMarginTable"; // ============================================================================
import { pluck } from "ramda";
import { useTranslation } from "react-i18next"; // ============================================================================

// ============================================================================
// Types
// ============================================================================jus

type RequireOnlyOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Record<Exclude<Keys, K>, never>>;
  }[Keys];

/**
 * Ratelists the table calculates a single margin category with. Either an already saved ratelist id
 * or an unsaved ratelist structure can be given, the structure wins when both are present.
 */
export type RatelistScope = {
  category: MarginCategory | `${MarginCategory}`;
  priceRatelistId?: string | null | undefined;
  wageRatelistId?: string | null | undefined;
  /**
   * Not yet saved price ratelist structure, takes precedence over `priceRatelistId`.
   */
  priceRatelist?: CalculatorRatelistInput | null | undefined;
  /**
   * Not yet saved wage ratelist structure, takes precedence over `wageRatelistId`.
   */
  wageRatelist?: CalculatorRatelistInput | null | undefined;
  filterDisabled?: boolean;
};

export interface RatesCompositionTableProps {
  ids: RequireOnlyOne<{
    orderId: string[] | null;
    positionId: string[] | null;
    shiftId: string[] | null;
    jobId: string[] | null;
  }>;
  ratelistScopes: RatelistScope[];
  groupBy?: { label: string; groups: MarginCategory[] }[];
  /**
   * How many levels of group rows are expanded on initial load. `0` keeps every group collapsed,
   * `-1` (the default) expands the whole tree.
   */
  expandedDepth?: number;
  /**
   * Evaluate the entities as the copy of their order moved by this many days (copy dialog preview).
   * Undefined or null renders the live composition.
   */
  copyPreviewDayShift?: number | null;
}

export type Row = {
  entityName: EntityName;
  path?: string[];
  label?: ReactNode;
  id: string;
  price: number;
  wage: number;
  total: number;
  priceDetails: RateComponentComposition[];
  wageDetails: RateComponentComposition[];
};

// ============================================================================
// Main Component
// ============================================================================

export const RatesCompositionTable: FC<RatesCompositionTableProps> = ({
  ids,
  ratelistScopes,
  groupBy,
  expandedDepth = -1,
  copyPreviewDayShift,
}) => {
  const apiRef = useGridApiRef();
  const [currentIds, setCurrentIds] = React.useState<RatesCompositionTableProps["ids"]>({ positionId: [] });

  const availableFilters = useMemo(
    () => ratelistScopes.filter(({ filterDisabled }) => !filterDisabled).map(({ category }) => category),
    [ratelistScopes]
  );

  const [categoryFilter, setCategoryFilter] = useState<Set<MarginCategory | `${MarginCategory}`>>(
    () => new Set(availableFilters)
  );

  const autosizeOptions = useMemo(
    () => ({
      columns: ["price", "wage", "total", "marginPercentage"],
      includeHeaders: true,
      includeOutliers: true,
      expand: true,
    }),
    []
  );

  const filteredScopes = useMemo(
    () => ratelistScopes.filter(({ category }) => categoryFilter.has(category)),
    [ratelistScopes, categoryFilter]
  );
  const filteredGroups = useMemo(
    () => groupBy?.filter(({ groups }) => groups.some((scope) => categoryFilter.has(scope))),
    [groupBy, categoryFilter]
  );

  const { loading, ...treeQueries } = useTreeQueries(currentIds, filteredScopes, filteredGroups, copyPreviewDayShift);

  const childrenGetter = useChildrenGetter();
  const labelGetter = useLabelGetter();
  const rows = useTreeRows(ids, treeQueries, childrenGetter, labelGetter);

  const prevLoadingRef = useRef(loading);

  const observer = useMemo(
    () =>
      new IntersectionObserver(
        // apiRef.current is read asynchronously inside setTimeout, never during render
        // oxlint-disable-next-line react-hooks-js/refs
        ([entry]) => {
          if (entry.isIntersecting) {
            setCurrentIds(ids);
            setTimeout(() => {
              apiRef.current?.autosizeColumns(autosizeOptions);
            }, 100);
          }
        },
        { threshold: 0.1 }
      ),
    [apiRef, autosizeOptions]
  );

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;
    const wasLoading = prevLoadingRef.current;
    prevLoadingRef.current = loading;

    if (wasLoading && !loading) {
      timer = setTimeout(() => {
        apiRef.current?.autosizeColumns(autosizeOptions);
      }, 100);
    }

    return () => {
      clearTimeout(timer);
    };
  }, [loading, apiRef, autosizeOptions]);

  const columns = useRatesColumns();

  const handleRowClick = useCallback<GridEventListener<"rowClick">>(
    (params) => {
      const currentNode = apiRef.current?.getRowNode(params.id) as GridBasicGroupNode | null;
      if (currentNode?.type === "group") {
        apiRef.current?.setRowChildrenExpansion(params.id, !currentNode?.childrenExpanded);
      }
    },
    [apiRef]
  );

  const getDetailPanelContent = useCallback(
    ({ row }: { row: Row }) => {
      const depth = (row.path?.length ?? 1) - 1;
      const isNotAttendance = row.entityName !== "Attendance";

      return (
        <Box sx={{ "--row-depth": depth }}>
          <InternalRateComponentsMarginTable
            hideDetails={isNotAttendance}
            parentApiRef={apiRef as MutableRefObject<GridApiPro>}
            priceDetails={row.priceDetails}
            wageDetails={row.wageDetails}
          />
        </Box>
      );
    },
    [apiRef]
  );

  const getDetailPanelHeight = useCallback(() => "auto" as const, []);

  const getRowClassName = useCallback(
    ({ id, row }: { id: string | number; row: Row }) => {
      const childrenCount = apiRef.current?.getRowGroupChildren({ groupId: id })?.length ?? 0;
      const isTotalRow = id === TOTAL_ROW_ID;
      return clsx(
        childrenCount > 0 && "TableLikeDataGrid-foldableRow",
        // The total row is not part of the hierarchy and keeps its own styling.
        !isTotalRow && getRowLevelClassName((row.path?.length ?? 1) - 1),
        isTotalRow ? "TableLikeDataGrid-totalRow" : "TableLikeDataGrid-row"
      );
    },
    [apiRef]
  );

  const groupingColDef: DataGridProProps["groupingColDef"] = useMemo(
    () => ({
      headerName: "",
      flex: 1,
      headerClassName: () => "TableLikeDataGrid-header-filter",
      display: "flex",
      cellClassName: () => "TableLikeDataGrid-grouping-cell",
      renderCell: (params) => <CustomGridTreeDataGroupingCell {...params} />,
      renderHeader: () => (
        <CategoryFilter
          availableFilters={pluck("category", ratelistScopes)}
          categoryFilter={categoryFilter}
          setCategoryFilter={setCategoryFilter}
        />
      ),
    }),
    [categoryFilter]
  );

  return (
    <CalculationDataGrid<Row>
      ref={(el) => {
        if (el) {
          observer.disconnect();
          observer.observe(el);
        }
      }}
      apiRef={apiRef}
      autosizeOptions={autosizeOptions}
      classes={{
        columnHeader: "TableLikeDataGrid-columnHeader",
        columnHeaderTitle: "TableLikeDataGrid-columnHeaderTitle",
      }}
      columnGroupHeaderHeight={46}
      columnHeaderHeight={46}
      columns={columns}
      defaultGroupingExpansionDepth={expandedDepth}
      density="compact"
      disableColumnFilter
      disableColumnMenu
      disableColumnReorder
      disableColumnResize
      disableColumnSelector
      disableColumnSorting
      disableRowSelectionOnClick
      disableVirtualization
      getCellClassName={(params) => "TableLikeDataGrid TableLikeDataGrid-depth-" + params.rowNode.depth}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      getRowClassName={getRowClassName}
      getTreeDataPath={(row: Row) => row.path ?? []}
      groupingColDef={groupingColDef}
      hideFooter
      loading={loading}
      onRowClick={handleRowClick}
      rows={rows}
      slots={{ noRowsOverlay: CustomNoRowsOverlay }}
      sx={{ width: "100%", height: "auto" }}
      treeData
    />
  );
};

function CustomNoRowsOverlay() {
  const { t } = useTranslation();
  return (
    <Box sx={{ p: 4, textAlign: "center" }}>
      <Typography variant="caption">{t("CategoryFilter.emptyFilter")}</Typography>
    </Box>
  );
}
